(() => {
  if (window.__dmBoldProductUxLoaded) return;
  window.__dmBoldProductUxLoaded = true;

  const pageClass = 'dm-bold-product-ux';
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const rootSelector = '.dm-product-bold-inspired';
  const rootCleanups = new WeakMap();
  let resizeObserver;

  const registerCleanup = (root, cleanup) => {
    const cleanups = rootCleanups.get(root) || [];
    cleanups.push(cleanup);
    rootCleanups.set(root, cleanups);
  };

  const cleanupRoot = (root) => {
    (rootCleanups.get(root) || []).forEach((cleanup) => cleanup());
    rootCleanups.delete(root);
    delete root.dataset.dmUxInitialized;
    delete root.dataset.dmPurchaseSignature;
  };

  const setText = (element, value) => {
    if (element && element.textContent.trim() !== value) element.textContent = value;
  };

  const cleanMoneyMarkup = (value) => {
    const template = document.createElement('template');
    template.innerHTML = value;
    return template.content.textContent.trim().replace(/\s+/g, ' ');
  };

  const toCentsOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const cents = Number(value);
    return Number.isFinite(cents) && cents >= 0 ? cents : null;
  };

  const formatMoney = (cents, root) => {
    const normalizedCents = toCentsOrNull(cents);
    if (normalizedCents === null || !window.Shopify?.formatMoney) return '';
    const moneyFormat = root?.dataset.moneyFormat || window.money_format || 'R$ {{amount_with_comma_separator}}';
    const formatted = cleanMoneyMarkup(window.Shopify.formatMoney(normalizedCents, moneyFormat));
    if (formatted.includes('R$')) return formatted;
    return 'R$ ' + formatted.replace(/\bBRL\b/g, '').trim();
  };

  const getMainForm = (root) => root.querySelector(
    'form[id^="product-form-"]:not([id^="product-form-installment-"]):not([id^="product-form-sticky-"])'
  );

  const getVariantData = (root) => {
    if (root.dmVariantData) return root.dmVariantData;
    const source = root.querySelector(
      'variant-radios script[type="application/json"], variant-selects script[type="application/json"]'
    );
    try {
      root.dmVariantData = source ? JSON.parse(source.textContent) : [];
    } catch (error) {
      root.dmVariantData = [];
    }
    return root.dmVariantData;
  };

  const getQuantityInput = (root, mainForm) => (
    root.querySelector('.dm-product-quantity input[name="quantity"]') ||
    mainForm?.querySelector('input[name="quantity"]') ||
    root.querySelector('.dm-product-buy input[name="quantity"]')
  );

  const getSellingPlanControl = (root) => {
    const controls = [...root.querySelectorAll('[name="selling_plan"]')].filter((control) => (
      !control.closest('[id^="product-form-sticky-"]') && !control.disabled
    ));

    return controls.find((control) => {
      if (control.matches('[type="radio"], [type="checkbox"]')) return control.checked;
      return control.value !== '';
    }) || controls.find((control) => (
      control.matches('[type="radio"], [type="checkbox"]') && control.checked
    )) || null;
  };

  const getRenderedVariantFallback = (root, variantId, quantityInput) => {
    if (!Number.isFinite(variantId) || variantId <= 0) return null;

    const mainButton = root.querySelector('#product-add-to-cart');
    const renderedPrice = Number(quantityInput?.dataset.price);
    const renderedCompare = Number(
      root.querySelector('.productView-price [data-compare]')?.dataset.compare
    );

    return {
      id: variantId,
      price: Number.isFinite(renderedPrice) ? renderedPrice : null,
      compare_at_price: Number.isFinite(renderedCompare) && renderedCompare > renderedPrice
        ? renderedCompare
        : null,
      available: !mainButton?.disabled,
      title: ''
    };
  };

  const getPurchaseState = (root) => {
    const mainForm = getMainForm(root);
    const variantInput = mainForm?.querySelector('[name="id"]');
    const formVariantId = Number(variantInput?.value);
    const quantityInput = getQuantityInput(root, mainForm);
    const variantData = getVariantData(root);
    const variantControl = root.querySelector('variant-radios, variant-selects');
    const hasLiveVariantState = Boolean(
      variantControl && Object.prototype.hasOwnProperty.call(variantControl, 'currentVariant')
    );
    const selectedVariant = hasLiveVariantState
      ? variantControl.currentVariant
      : variantData.find((item) => Number(item.id) === formVariantId);
    const variant = selectedVariant || (
      !hasLiveVariantState && variantData.length === 0
        ? getRenderedVariantFallback(root, formVariantId, quantityInput)
        : null
    );
    const variantId = variant ? Number(variant.id) : null;
    const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
    const sellingPlanControl = getSellingPlanControl(root);
    const sellingPlan = sellingPlanControl?.value || '';

    return {
      mainForm,
      variant,
      variantId,
      quantity,
      quantityInput,
      sellingPlan,
      sellingPlanControl
    };
  };

  const resolvePricing = (state) => {
    const variant = state.variant;
    if (!variant) return { price: null, compareAtPrice: null, saving: null, savingPercent: null };

    const allocations = Array.isArray(variant.selling_plan_allocations) ? variant.selling_plan_allocations : [];
    const allocation = state.sellingPlan
      ? allocations.find((item) => String(item.selling_plan_id) === String(state.sellingPlan))
      : null;
    const controlPrice = toCentsOrNull(
      state.sellingPlanControl?.dataset.sellingPlanPrice ||
      state.sellingPlanControl?.dataset.price
    );
    const controlCompare = toCentsOrNull(
      state.sellingPlanControl?.dataset.sellingPlanCompareAtPrice ||
      state.sellingPlanControl?.dataset.compareAtPrice
    );
    const price = controlPrice ?? toCentsOrNull(allocation?.price ?? variant.price);
    const rawCompare = controlCompare ?? toCentsOrNull(
      allocation?.compare_at_price ?? variant.compare_at_price
    );
    const compareAtPrice = price !== null && rawCompare !== null && rawCompare > price
      ? rawCompare
      : null;
    const saving = compareAtPrice ? compareAtPrice - price : null;
    const savingPercent = compareAtPrice ? Math.round((saving / compareAtPrice) * 100) : null;

    return { price, compareAtPrice, saving, savingPercent };
  };

  const updatePageOffsets = () => {
    const toolbar = document.querySelector('.halo-sticky-toolbar-mobile');
    const sticky = document.querySelector(rootSelector + ' .productView-stickyCart.style-1');
    const stickyActive = document.documentElement.classList.contains('dm-sticky-cta-active')
      && !document.documentElement.classList.contains('dm-main-cta-visible');
    const toolbarHeight = mobileQuery.matches && toolbar && !stickyActive
      ? Math.ceil(toolbar.getBoundingClientRect().height)
      : 0;
    const stickyVisible = mobileQuery.matches && sticky && stickyActive;
    const stickyHeight = stickyVisible ? Math.ceil(sticky.getBoundingClientRect().height) : 0;

    document.documentElement.style.setProperty('--dm-toolbar-offset', toolbarHeight + 'px');
    document.documentElement.style.setProperty('--dm-sticky-offset', stickyHeight + 'px');
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-image-gallery-main]');
    const thumbnailRail = root.querySelector(
      '.productView-images-wrapper > .productView-thumbnail-wrapper'
    );
    if (!gallery || !thumbnailRail) return;

    const getThumbnails = () => [...thumbnailRail.querySelectorAll(
      '.productView-thumbnail:not(.slick-cloned)'
    )];
    const getMediaItems = () => [...gallery.querySelectorAll(
      '.productView-image:not(.slick-cloned)'
    )];

    const updateGalleryState = (index = 0) => {
      const thumbnails = getThumbnails();
      const mediaItems = getMediaItems();
      const mediaControls = mediaItems
        .map((item) => item.querySelector('.media[role="button"]'))
        .filter(Boolean);
      const total = Math.max(thumbnails.length, mediaItems.length, 1);
      const numericIndex = Number.isFinite(Number(index)) ? Math.trunc(Number(index)) : 0;
      const normalizedIndex = ((numericIndex % total) + total) % total;

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const control = thumbnail.querySelector('.productView-thumbnail-link');
        const selected = thumbnailIndex === normalizedIndex;
        if (!control) return;
        control.setAttribute('role', 'button');
        control.setAttribute('tabindex', '0');
        control.setAttribute('aria-label', 'Mostrar imagem ' + (thumbnailIndex + 1) + ' de ' + total);
        control.setAttribute('aria-current', selected ? 'true' : 'false');
        control.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const selectedMediaId = thumbnails[normalizedIndex]?.dataset.mediaId;
      thumbnailRail.querySelectorAll('.productView-thumbnail').forEach((thumbnail) => {
        thumbnail.classList.toggle(
          'dm-thumbnail-is-current',
          Boolean(selectedMediaId) && thumbnail.dataset.mediaId === selectedMediaId
        );
      });

      mediaControls.forEach((control, mediaIndex) => {
        control.setAttribute('tabindex', mediaIndex === normalizedIndex ? '0' : '-1');
      });
    };

    const getSlickInstance = () => gallery.slick || null;

    const getCurrentIndex = () => {
      const slick = getSlickInstance();
      if (Number.isFinite(slick?.currentSlide)) return slick.currentSlide;
      return Math.max(0, getThumbnails().findIndex(
        (thumbnail) => thumbnail.querySelector('[aria-current="true"]')
      ));
    };

    /**
     * Com o carrossel ativo, a miniatura troca o slide. Sem ele (imagens empilhadas no
     * desktop), rola até a imagem por âncora; o pedido fica guardado caso o carrossel
     * ainda vá iniciar.
     */
    const selectGalleryMedia = (index) => {
      if (window.jQuery && getSlickInstance()) {
        window.jQuery(gallery).slick('slickGoTo', index);
        updateGalleryState(index);
        return;
      }

      gallery.dataset.dmPendingSlide = String(index);
      const target = getMediaItems()[index];
      if (!target) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      updateGalleryState(index);
    };

    const clickHandler = (event) => {
      const control = event.target.closest('.productView-thumbnail-link');
      const thumbnail = control?.closest('.productView-thumbnail');
      if (!thumbnail || !thumbnailRail.contains(thumbnail)) return;

      const index = getThumbnails().indexOf(thumbnail);
      if (index < 0) return;
      event.preventDefault();
      selectGalleryMedia(index);
    };

    const keydownHandler = (event) => {
      const control = event.target.closest(
        '.productView-thumbnail-link, .productView-img-container .media[role="button"]'
      );
      if (!control || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      control.click();
    };

    root.addEventListener('click', clickHandler);
    root.addEventListener('keydown', keydownHandler);
    registerCleanup(root, () => {
      root.removeEventListener('click', clickHandler);
      root.removeEventListener('keydown', keydownHandler);
      thumbnailRail.querySelectorAll('.dm-thumbnail-is-current').forEach((thumbnail) => {
        thumbnail.classList.remove('dm-thumbnail-is-current');
      });
    });

    // Sem carrossel (imagens empilhadas no desktop), a miniatura marcada acompanha a
    // imagem mais visível na tela.
    if ('IntersectionObserver' in window) {
      const visibility = new Map();
      const observer = new IntersectionObserver((entries) => {
        if (getSlickInstance()) return;
        entries.forEach((entry) => visibility.set(entry.target, entry.intersectionRatio));
        let current = -1;
        let currentRatio = 0;
        getMediaItems().forEach((item, index) => {
          const ratio = visibility.get(item) || 0;
          if (ratio > currentRatio) {
            currentRatio = ratio;
            current = index;
          }
        });
        if (current >= 0) updateGalleryState(current);
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
      getMediaItems().forEach((item) => observer.observe(item));
      registerCleanup(root, () => observer.disconnect());
    }

    if (window.jQuery) {
      const slider = window.jQuery(gallery);
      slider
        .off('.dmBoldProductUx')
        .on('init.dmBoldProductUx reInit.dmBoldProductUx afterChange.dmBoldProductUx', (
          event,
          _slick,
          currentSlide
        ) => {
          const pendingSlide = Number(gallery.dataset.dmPendingSlide);
          if (event.type !== 'afterChange' && Number.isFinite(pendingSlide)) {
            delete gallery.dataset.dmPendingSlide;
            window.requestAnimationFrame(() => selectGalleryMedia(pendingSlide));
            return;
          }
          updateGalleryState(currentSlide ?? _slick?.currentSlide ?? getCurrentIndex());
        });
      registerCleanup(root, () => slider.off('.dmBoldProductUx'));
    }

    window.requestAnimationFrame(() => updateGalleryState(getCurrentIndex()));
  };

  const syncStickyForm = (root, state) => {
    const stickyForm = root.querySelector('.productView-stickyCart form[id^="product-form-sticky-"]');
    if (!stickyForm) return;

    const stickyVariant = stickyForm.querySelector('[name="id"]');
    if (stickyVariant && state.variantId && stickyVariant.value !== String(state.variantId)) {
      stickyVariant.value = String(state.variantId);
      stickyVariant.dispatchEvent(new Event('change', { bubbles: false }));
    }

    const stickyQuantity = stickyForm.querySelector('[name="quantity"]');
    if (stickyQuantity) stickyQuantity.value = String(state.quantity);

    let stickySellingPlan = stickyForm.querySelector('[name="selling_plan"]');
    if (state.sellingPlan) {
      if (!stickySellingPlan) {
        stickySellingPlan = document.createElement('input');
        stickySellingPlan.type = 'hidden';
        stickySellingPlan.name = 'selling_plan';
        stickySellingPlan.dataset.dmStickySellingPlan = 'true';
        stickyForm.append(stickySellingPlan);
      }
      stickySellingPlan.value = state.sellingPlan;
      stickySellingPlan.disabled = false;
    } else if (stickySellingPlan?.dataset.dmStickySellingPlan === 'true') {
      stickySellingPlan.remove();
    } else if (stickySellingPlan) {
      stickySellingPlan.value = '';
    }
  };

  const syncStickyPrice = (root, state, pricing) => {
    const currentText = formatMoney(pricing.price, root);
    const compareText = formatMoney(pricing.compareAtPrice, root);
    const savingText = pricing.saving
      ? 'Economize ' + formatMoney(pricing.saving, root)
      : '';

    root.querySelectorAll('.productView-stickyCart .sticky-price').forEach((priceBlock) => {
      const currentWrapper = priceBlock.querySelector('.money-subtotal');
      const currentTarget = currentWrapper?.querySelector('.money') || currentWrapper;
      priceBlock.hidden = !currentText;
      if (!currentText) {
        priceBlock.querySelector('.money-compare-price')?.remove();
        priceBlock.classList.remove('has-compare-price');
        return;
      }
      if (currentTarget) setText(currentTarget, currentText);

      let compareWrapper = priceBlock.querySelector('.money-compare-price');
      if (compareText) {
        if (!compareWrapper) {
          compareWrapper = document.createElement('s');
          compareWrapper.className = 'money-compare-price';
          compareWrapper.dataset.dmStickyCompare = 'true';
          priceBlock.insertBefore(compareWrapper, currentWrapper);
        }
        const compareTarget = compareWrapper.querySelector('.money') || compareWrapper;
        setText(compareTarget, compareText);
        compareWrapper.dataset.comparePrice = String(pricing.compareAtPrice);
        priceBlock.classList.add('has-compare-price');
      } else {
        compareWrapper?.remove();
        priceBlock.classList.remove('has-compare-price');
      }

      const savings = priceBlock.querySelector('[data-dm-sticky-savings]');
      if (savings) {
        savings.hidden = !savingText;
        setText(savings, savingText);
      }

      priceBlock.dataset.variantId = state.variantId ? String(state.variantId) : '';
      priceBlock.setAttribute('role', 'status');
      priceBlock.setAttribute('aria-live', 'polite');
      priceBlock.setAttribute('aria-atomic', 'true');
    });
  };

  const syncStickyAvailability = (root, state) => {
    const mainButton = root.querySelector('#product-add-to-cart');
    const stickyButtons = root.querySelectorAll('#show-sticky-product, #product-sticky-add-to-cart');
    const unavailable = !state.variant;
    const soldOut = Boolean(state.variant && !state.variant.available);
    const variantUnavailable = unavailable || soldOut;
    if (mainButton && mainButton.getAttribute('aria-busy') !== 'true') {
      if (variantUnavailable) {
        // Só escreve quando o valor muda de fato. reflectButtonState roda dentro de
        // um MutationObserver que observa 'disabled' neste mesmo botão, e reescrever
        // o atributo enfileira um registro de mutação mesmo sem mudar o valor — o
        // callback se realimenta. Em produto esgotado a condição é permanente, então
        // sem esta guarda o ciclo não termina e trava a aba.
        if (!mainButton.disabled) mainButton.disabled = true;
        if (mainButton.dataset.dmAvailabilityDisabled !== 'true') {
          mainButton.dataset.dmAvailabilityDisabled = 'true';
        }
      } else if (mainButton.dataset.dmAvailabilityDisabled === 'true') {
        mainButton.disabled = false;
        delete mainButton.dataset.dmAvailabilityDisabled;
      }
    }
    const disabled = variantUnavailable || Boolean(mainButton?.disabled);
    const unavailableLabel = window.variantStrings?.unavailable || 'Indisponível';
    const soldOutLabel = window.variantStrings?.soldOut || 'Esgotado';
    const mainLabel = unavailable
      ? unavailableLabel
      : soldOut
        ? soldOutLabel
        : 'Comprar';
    const stickyLabel = unavailable
      ? unavailableLabel
      : soldOut
        ? soldOutLabel
        : 'Comprar';

    if (mainButton?.getAttribute('aria-busy') !== 'true') {
      const mainText = mainButton.querySelector('.button-text') || mainButton;
      setText(mainText, mainLabel);
    }

    stickyButtons.forEach((button) => {
      button.disabled = disabled;
      button.dataset.variantId = state.variantId ? String(state.variantId) : '';
      button.dataset.sellingPlan = state.sellingPlan;
      if (button.getAttribute('aria-busy') !== 'true') setText(button, stickyLabel);
    });

    const sticky = root.querySelector('.productView-stickyCart');
    if (sticky) {
      sticky.dataset.variantId = state.variantId ? String(state.variantId) : '';
      sticky.dataset.variantTitle = state.variant?.title || '';
      sticky.dataset.sellingPlan = state.sellingPlan;
      sticky.dataset.available = String(!disabled);
      sticky.dataset.quantity = String(state.quantity);
    }
  };

  const applyPurchaseState = (root, state) => {
    const pricing = resolvePricing(state);
    syncStickyForm(root, state);
    syncStickyPrice(root, state, pricing);
    syncStickyAvailability(root, state);
    updatePageOffsets();
  };

  const publishPurchaseState = (root, force = false) => {
    const state = getPurchaseState(root);
    const signature = [
      state.variantId || '',
      state.quantity,
      state.sellingPlan,
      state.variant?.available ? 'available' : 'unavailable'
    ].join('|');
    if (!force && root.dataset.dmPurchaseSignature === signature) return;
    root.dataset.dmPurchaseSignature = signature;

    root.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        variant: state.variant,
        variantId: state.variantId || null,
        quantity: state.quantity,
        sellingPlan: state.sellingPlan || null,
        purchaseType: state.sellingPlan ? 'subscription' : 'one-time',
        sourceForm: state.mainForm
      }
    }));
  };

  const initPurchaseState = (root) => {
    let syncFrame;
    const schedulePublish = (force = false) => {
      window.cancelAnimationFrame(syncFrame);
      syncFrame = window.requestAnimationFrame(() => publishPurchaseState(root, force));
    };
    const stateHandler = (event) => {
      if (event.target !== root || !event.detail) return;
      applyPurchaseState(root, getPurchaseState(root));
    };
    const changeHandler = (event) => {
      if (!event.target.matches(
        'variant-radios input, variant-selects select, [name="id"], [name="quantity"], [name="selling_plan"]'
      )) return;
      schedulePublish();
    };
    const inputHandler = (event) => {
      if (event.target.matches('[name="quantity"]')) schedulePublish();
    };

    root.addEventListener('variant:change', stateHandler);
    root.addEventListener('change', changeHandler);
    root.addEventListener('input', inputHandler);
    registerCleanup(root, () => {
      window.cancelAnimationFrame(syncFrame);
      root.removeEventListener('variant:change', stateHandler);
      root.removeEventListener('change', changeHandler);
      root.removeEventListener('input', inputHandler);
    });

    schedulePublish(true);
  };

  const initPurchaseFeedback = (root) => {
    const mainButton = root.querySelector('#product-add-to-cart');
    const stickyButtons = [...root.querySelectorAll('#show-sticky-product, #product-sticky-add-to-cart')];
    if (!mainButton) return;

    const productFormId = getMainForm(root)?.getAttribute('id') || '';
    const productId = productFormId.replace('product-form-', '') || 'product';
    const statusId = 'dm-product-purchase-status-' + productId;
    let status = root.querySelector('#' + statusId);
    if (!status) {
      status = document.createElement('span');
      status.id = statusId;
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      mainButton.parentElement?.append(status);
    }
    mainButton.setAttribute('aria-describedby', statusId);
    stickyButtons.forEach((button) => button.setAttribute('aria-describedby', statusId));

    const reflectButtonState = () => {
      const loading = mainButton.classList.contains('is-loading') || mainButton.classList.contains('loading');
      const state = getPurchaseState(root);

      mainButton.setAttribute('aria-busy', String(loading));
      stickyButtons.forEach((button) => {
        button.setAttribute('aria-busy', String(loading));
        button.disabled = loading || mainButton.disabled || !state.variant?.available;
      });

      if (loading) {
        stickyButtons.forEach((button) => setText(button, 'Adicionando…'));
        setText(status, 'Adicionando produto ao carrinho');
        return;
      }

      setText(status, '');
      syncStickyAvailability(root, state);
    };

    const mainClickHandler = () => {
      setText(status, 'Adicionando produto ao carrinho');
      window.requestAnimationFrame(reflectButtonState);
    };
    const stickyClickHandler = (event) => {
      const stickyButton = event.currentTarget;
      if (stickyButton.disabled || mainButton.disabled || mainButton.getAttribute('aria-busy') === 'true') return;
      if (
        stickyButton.id === 'show-sticky-product'
        && getVariantData(root).length > 1
      ) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      mainButton.click();
    };
    const stateHandler = (event) => {
      if (event.target === root) reflectButtonState();
    };

    mainButton.addEventListener('click', mainClickHandler);
    stickyButtons.forEach((button) => button.addEventListener('click', stickyClickHandler, true));
    root.addEventListener('variant:change', stateHandler);

    const buttonObserver = new MutationObserver(reflectButtonState);
    buttonObserver.observe(mainButton, {
      attributes: true,
      attributeFilter: ['class', 'disabled']
    });

    let visibilityFrame = 0;
    const syncCtaVisibility = () => {
      window.cancelAnimationFrame(visibilityFrame);
      visibilityFrame = window.requestAnimationFrame(() => {
        const bounds = mainButton.getBoundingClientRect();
        const toolbar = document.querySelector('.halo-sticky-toolbar-mobile');
        const toolbarBounds = toolbar?.getBoundingClientRect();
        const hasVisibleToolbar = Boolean(toolbarBounds?.height);
        const visibleViewportBottom = hasVisibleToolbar ? toolbarBounds.top : window.innerHeight;
        const mainCtaVisible = bounds.bottom > 0 && bounds.bottom <= visibleViewportBottom;
        const stickyCtaActive = bounds.bottom <= 0 || (
          hasVisibleToolbar &&
          bounds.top < window.innerHeight &&
          bounds.bottom > visibleViewportBottom
        );
        document.documentElement.classList.toggle('dm-main-cta-visible', mainCtaVisible);
        document.documentElement.classList.toggle('dm-sticky-cta-active', stickyCtaActive);
        updatePageOffsets();
      });
    };

    window.addEventListener('scroll', syncCtaVisibility, { passive: true });
    window.addEventListener('resize', syncCtaVisibility, { passive: true });
    syncCtaVisibility();

    registerCleanup(root, () => {
      buttonObserver.disconnect();
      window.cancelAnimationFrame(visibilityFrame);
      window.removeEventListener('scroll', syncCtaVisibility);
      window.removeEventListener('resize', syncCtaVisibility);
      mainButton.removeEventListener('click', mainClickHandler);
      stickyButtons.forEach((button) => button.removeEventListener('click', stickyClickHandler, true));
      root.removeEventListener('variant:change', stateHandler);
      document.documentElement.classList.remove('dm-main-cta-visible', 'dm-sticky-cta-active');
    });
  };


  const initRelatedProducts = (root) => {
    root.querySelectorAll('[data-dm-related-products]').forEach((component) => {
      const trigger = component.querySelector('[data-dm-related-products-trigger]');
      const options = component.querySelector('[data-dm-related-products-options]');
      const links = [...component.querySelectorAll('.dm-related-products__option')];
      if (!trigger || !options || !links.length) return;

      const setOpen = (open, focusOption = false) => {
        component.dataset.open = String(open);
        trigger.setAttribute('aria-expanded', String(open));
        options.hidden = !open;
        if (open && focusOption) {
          const current = links.find((link) => link.getAttribute('aria-selected') === 'true') || links[0];
          window.requestAnimationFrame(() => current.focus());
        }
      };
      const moveFocus = (current, offset) => {
        const currentIndex = Math.max(0, links.indexOf(current));
        links[(currentIndex + offset + links.length) % links.length].focus();
      };
      const triggerClickHandler = () => setOpen(options.hidden, true);
      const triggerKeyHandler = (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        setOpen(true);
        const target = event.key === 'ArrowUp' || event.key === 'End' ? links.at(-1) : links[0];
        window.requestAnimationFrame(() => target.focus());
      };
      const optionsKeyHandler = (event) => {
        const link = event.target.closest('.dm-related-products__option');
        if (!link) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
          trigger.focus();
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          moveFocus(link, event.key === 'ArrowDown' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          links[event.key === 'Home' ? 0 : links.length - 1].focus();
        } else if (event.key === ' ') {
          event.preventDefault();
          link.click();
        } else if (event.key === 'Tab') {
          setOpen(false);
        }
      };
      const outsideHandler = (event) => {
        if (!component.contains(event.target)) setOpen(false);
      };

      trigger.addEventListener('click', triggerClickHandler);
      trigger.addEventListener('keydown', triggerKeyHandler);
      options.addEventListener('keydown', optionsKeyHandler);
      document.addEventListener('pointerdown', outsideHandler);
      registerCleanup(root, () => {
        trigger.removeEventListener('click', triggerClickHandler);
        trigger.removeEventListener('keydown', triggerKeyHandler);
        options.removeEventListener('keydown', optionsKeyHandler);
        document.removeEventListener('pointerdown', outsideHandler);
      });
    });
  };

  const initAccessibleControls = (root) => {
    const shareButton = root.querySelector('.dm-product-title-row .share-button__button');
    if (shareButton) {
      shareButton.setAttribute('aria-label', 'Compartilhar produto');
      shareButton.setAttribute('title', 'Compartilhar produto');
    }

    root.querySelectorAll('[data-header-option]').forEach((label) => {
      label.setAttribute('aria-live', 'polite');
      label.setAttribute('aria-atomic', 'true');
    });

    root.querySelectorAll('.assinatura-button-wrapper button').forEach((button) => {
      button.type = 'button';
    });
  };

  const initDescriptionReadMore = (root) => {
    root.querySelectorAll('[data-dm-description-toggle]').forEach((button) => {
      const content = button.closest('.toggle-content, .dm-product-short-description');
      const preview = content?.querySelector('[data-dm-description-preview]');
      const full = content?.querySelector('[data-dm-description-full]');
      if (!content || !preview || !full) return;

      const clickHandler = () => {
        const willExpand = button.getAttribute('aria-expanded') !== 'true';
        preview.hidden = willExpand;
        full.hidden = !willExpand;
        button.setAttribute('aria-expanded', String(willExpand));
        button.textContent = willExpand ? 'Ocultar descrição' : 'Continuar lendo';
      };

      button.addEventListener('click', clickHandler);
      registerCleanup(root, () => button.removeEventListener('click', clickHandler));
    });
  };

  const initDesktopThumbnailAlignment = (root) => {
    const desktopQuery = window.matchMedia('(min-width: 1280px)');
    const thumbnailRail = root.querySelector(
      '.productView-images-wrapper > .productView-thumbnail-wrapper'
    );
    const imageWrapper = root.querySelector(
      '.productView-images-wrapper > .productView-image-wrapper'
    );
    const shippingCard = root.querySelector('.frete-calculo');
    if (!thumbnailRail || !imageWrapper || !shippingCard) return;

    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const railBounds = thumbnailRail.getBoundingClientRect();
        if (!desktopQuery.matches || !root.querySelector('.productView.layout-4')
          || railBounds.height <= 0 || railBounds.width <= 0
          || window.getComputedStyle(thumbnailRail).visibility === 'hidden') {
          imageWrapper.style.removeProperty('--dm-product-gallery-image-height');
          return;
        }

        // Use fixed anchors, not the wrapper height changed by this calculation.
        const wrapperTop = imageWrapper.getBoundingClientRect().top;
        const shippingBottom = shippingCard.getBoundingClientRect().bottom;
        const railMarginTop = Number.parseFloat(window.getComputedStyle(thumbnailRail).marginTop) || 0;
        const wrapperMarginBottom = Number.parseFloat(window.getComputedStyle(imageWrapper).marginBottom) || 0;
        const targetHeight = Math.max(0, Math.round(
          shippingBottom - wrapperTop - railBounds.height - railMarginTop - wrapperMarginBottom
        ));
        const heightValue = targetHeight + 'px';
        if (imageWrapper.style.getPropertyValue('--dm-product-gallery-image-height') !== heightValue) {
          imageWrapper.style.setProperty('--dm-product-gallery-image-height', heightValue);
        }
      });
    };

    const resizeObserver = 'ResizeObserver' in window
      ? new ResizeObserver(update)
      : null;
    resizeObserver?.observe(shippingCard);
    resizeObserver?.observe(root.querySelector('.halo-productView-right') || shippingCard);
    resizeObserver?.observe(imageWrapper);
    resizeObserver?.observe(thumbnailRail);

    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(shippingCard, { childList: true, subtree: true });

    window.addEventListener('resize', update, { passive: true });
    desktopQuery.addEventListener?.('change', update);
    window.requestAnimationFrame(update);

    registerCleanup(root, () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', update);
      desktopQuery.removeEventListener?.('change', update);
      imageWrapper.style.removeProperty('--dm-product-gallery-image-height');
    });
  };

  const initFloatingControlSafety = (root) => {
    if (!('IntersectionObserver' in window)) return;

    const protectedControls = [...root.querySelectorAll([
      '.dm-product-price',
      '.dm-product-buy',
      '.dm-product-quantity',
      '.assinatura-divisor',
      '.assinatura-button-wrapper',
      '.frete-produto',
      '.shipping-calc-wrapper'
    ].join(', '))];
    if (!protectedControls.length) return;

    const visibleControls = new Map(protectedControls.map((control) => [control, false]));
    const updateSafetyState = () => {
      const hasVisibleControl = [...visibleControls.values()].some(Boolean);
      document.documentElement.classList.toggle('dm-product-commerce-visible', hasVisibleControl);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visibleControls.set(entry.target, entry.isIntersecting && entry.intersectionRatio > 0);
      });
      updateSafetyState();
    }, { threshold: [0, 0.01] });

    protectedControls.forEach((control) => observer.observe(control));
    registerCleanup(root, () => {
      observer.disconnect();
      document.documentElement.classList.remove('dm-product-commerce-visible');
    });
  };

  const init = () => {
    const roots = [...document.querySelectorAll(rootSelector)];
    if (!roots.length) return;
    document.documentElement.classList.add(pageClass);

    roots.forEach((root) => {
      if (root.dataset.dmUxInitialized === 'true') return;
      root.dataset.dmUxInitialized = 'true';
      initGallery(root);
      initRelatedProducts(root);
      initPurchaseState(root);
      initPurchaseFeedback(root);
      initDescriptionReadMore(root);
      initDesktopThumbnailAlignment(root);
      initAccessibleControls(root);
      initFloatingControlSafety(root);
    });

    updatePageOffsets();
    const toolbar = document.querySelector('.halo-sticky-toolbar-mobile');
    const sticky = document.querySelector(rootSelector + ' .productView-stickyCart.style-1');
    if ('ResizeObserver' in window) {
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(updatePageOffsets);
      if (toolbar) resizeObserver.observe(toolbar);
      if (sticky) resizeObserver.observe(sticky);
    }
  };

  const refreshAllPurchaseStates = () => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll(rootSelector).forEach((root) => publishPurchaseState(root, true));
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.addEventListener('resize', updatePageOffsets, { passive: true });
  window.addEventListener('pageshow', refreshAllPurchaseStates);
  window.addEventListener('popstate', refreshAllPurchaseStates);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [
      ...(event.target.matches?.(rootSelector) ? [event.target] : []),
      ...event.target.querySelectorAll(rootSelector)
    ];
    roots.forEach(cleanupRoot);

    window.requestAnimationFrame(() => {
      if (document.querySelector(rootSelector)) {
        init();
      } else {
        resizeObserver?.disconnect();
        document.documentElement.classList.remove(pageClass);
        document.documentElement.classList.remove(
          'dm-main-cta-visible',
          'dm-sticky-cta-active',
          'dm-product-commerce-visible'
        );
        document.documentElement.style.removeProperty('--dm-toolbar-offset');
        document.documentElement.style.removeProperty('--dm-sticky-offset');
      }
    });
  });
})();
